/**
 * SanPlumb — LeftPanel del visor (Fase 3d)
 *
 * Tres secciones: resumen Hunter (Q/Ø/velocidad del engine), niveles NPT
 * clave y selección de marcas del catálogo.
 *
 * Portado de `screens.jsx` (LeftPanel) del diseño aprobado.
 */

import type { BrandState, UsoMode, CountsState } from '../../types/ui';
import { calcUG } from '../../constants/aparatosUI';
import { interpolateHunter, getVelocity, selectDiameterByVelocity } from '../../engine/hunter';
import { NPT_HEIGHTS } from '../../constants/paeba';
import { BRANDS } from '../../constants/ui';
import { VELOCIDADES_MAX } from '../../constants/is010';

export interface LeftPanelProps {
  counts: CountsState;
  mode: UsoMode;
  brand: BrandState;
  setBrand: (b: BrandState) => void;
}

/** Niveles NPT clave a mostrar (id → etiqueta + sistema). */
const NPT_KEYS: readonly { key: keyof typeof NPT_HEIGHTS; label: string; sys: 'af' | 'ac' }[] = [
  { key: 'alimentacion_af', label: 'Alimentación AF', sys: 'af' },
  { key: 'lavatorio_af', label: 'Salida lavatorio', sys: 'af' },
  { key: 'ducha_llave', label: 'Llave ducha', sys: 'ac' },
  { key: 'ducha_brazo', label: 'Brazo ducha', sys: 'af' },
  { key: 'inodoro_af', label: 'Tanque inodoro', sys: 'af' },
];

export function LeftPanel({ counts, mode, brand, setBrand }: LeftPanelProps): JSX.Element {
  const { af, ac, ug } = calcUG(counts, mode);
  const q = interpolateHunter(ug);
  const sel = selectDiameterByVelocity(q);
  const v = getVelocity(q, sel.mm);
  const vMax = VELOCIDADES_MAX[sel.mm] ?? 2.2;

  return (
    <div className="left-panel">
      <div className="panel-scroll">
        {/* Resumen Hunter */}
        <div className="lp-section">
          <div className="lp-h">▸ Cálculo Hunter</div>
          <div className="pipe-row">
            <span className="t-af">AF</span>
            <span>UG agua fría</span>
            <span className="mono">{af}</span>
          </div>
          <div className="pipe-row">
            <span className="t-ac">AC</span>
            <span>UG agua caliente</span>
            <span className="mono">{ac}</span>
          </div>
          <div className="pipe-row">
            <span className="mono">Q</span>
            <span>Caudal probable</span>
            <span className="mono">{q} L/s</span>
          </div>
          <div className="pipe-row">
            <span className="mono">Ø</span>
            <span>Alimentación</span>
            <span className="mono">{sel.d}</span>
          </div>
          <div className="pipe-row">
            <span className="mono">v</span>
            <span>Velocidad</span>
            <span className="mono" style={{ color: v <= vMax ? 'var(--ok)' : 'var(--warn)' }}>
              {v} m/s
            </span>
          </div>
        </div>

        {/* Niveles NPT */}
        <div className="lp-section">
          <div className="lp-h">▸ Niveles NPT</div>
          {NPT_KEYS.map((n) => (
            <div className="pipe-row" key={n.key}>
              <span className={n.sys === 'ac' ? 't-ac' : 't-af'}>{n.sys === 'ac' ? 'AC' : 'AF'}</span>
              <span>{n.label}</span>
              <span className="mono">+{NPT_HEIGHTS[n.key].toFixed(2)}m</span>
            </div>
          ))}
        </div>

        {/* Catálogo */}
        <div className="lp-section">
          <div className="lp-h">▸ Catálogo / Marcas</div>
          {(Object.keys(BRANDS) as (keyof BrandState)[]).map((cat) => (
            <div className="pipe-row" key={cat}>
              <span style={{ fontSize: 9, color: 'var(--t-mut)' }}>
                {cat === 'pipeAF'
                  ? 'Tub AF'
                  : cat === 'pipeAC'
                    ? 'Tub AC'
                    : cat === 'valveAF'
                      ? 'Válv AF'
                      : cat === 'valveAC'
                        ? 'Válv AC'
                        : 'Aparatos'}
              </span>
              <select
                className="form-input"
                value={brand[cat]}
                onChange={(e) => setBrand({ ...brand, [cat]: e.target.value })}
                style={{ gridColumn: '2 / 4', width: '100%' }}
              >
                {BRANDS[cat].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
